import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { FiSearch, FiBell, FiMoon, FiSun, FiUser, FiGrid, FiBookOpen } from 'react-icons/fi';

// Sample data
const stats = [
  { id: 1, title: 'Courses', value: 12, icon: FiBookOpen, color: '#2563eb' },  // blue-600
  { id: 2, title: 'Lessons Completed', value: 86, icon: FiGrid, color: '#16a34a' }, // green-600
  { id: 3, title: 'Certificates', value: 3, icon: FiUser, color: '#f59e0b' }, // amber-500
];

const progressData = [
  { name: 'Jan', progress: 20 },
  { name: 'Feb', progress: 35 },
  { name: 'Mar', progress: 50 },
  { name: 'Apr', progress: 60 },
  { name: 'May', progress: 72 },
  { name: 'Jun', progress: 80 },
  { name: 'Jul', progress: 90 },
];

const courses = [
  { id: 1, title: 'React — From Zero to Hero', author: 'Ali Rezai', progress: 72, level: 'Intermediate' },
  { id: 2, title: 'Advanced CSS Animations', author: 'Sara Majid', progress: 40, level: 'Advanced' },
  { id: 3, title: 'JavaScript Deep Dive', author: 'Hamid Karimi', progress: 12, level: 'Beginner' },
  { id: 4, title: 'Product Design Fundamentals', author: 'Leila Noor', progress: 55, level: 'Intermediate' },
];

const activities = [
  { id: 1, text: 'Completed lesson 3 of React — From Zero to Hero', time: '2h ago' },
  { id: 2, text: 'New quiz unlocked in JavaScript Deep Dive', time: '1d ago' },
  { id: 3, text: 'Certificate earned: Advanced CSS Animations', time: '3d ago' },
];

const recommendations = [
  'TypeScript for Professionals',
  'Fullstack with Next.js',
  'Design Systems with Figma',
  'Testing React Apps',
];

const COLORS = ['#1976d2', '#388e3c', '#f57c00'];

// Utility function for class join removed — now not needed.

// Main Component
export default function ProLearningDashboard() {
  const [dark, setDark] = useState(false);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('All');

  useEffect(() => {
    const saved = localStorage.getItem('prodash_dark');
    if (saved) setDark(saved === 'true');
  }, []);

  useEffect(() => {
    if (dark) {
      document.body.style.backgroundColor = '#1a202c'; // dark gray bg
      document.body.style.color = '#e2e8f0'; // light text
    } else {
      document.body.style.backgroundColor = '#f9fafb'; // light bg
      document.body.style.color = '#1a202c'; // dark text
    }
    localStorage.setItem('prodash_dark', dark);
  }, [dark]);

  const filteredCourses = courses.filter((c) =>
    (filter === 'All' || c.level === filter) && c.title.toLowerCase().includes(query.toLowerCase())
  );

  // Common styles
  const cardStyle = {
    backgroundColor: dark ? '#2d3748' : '#fff',
    borderRadius: 16,
    padding: 16,
    boxShadow: dark ? '0 1px 4px rgba(0,0,0,0.7)' : '0 1px 6px rgba(0,0,0,0.1)',
    marginBottom: 16,
  };

  return (
    <div style={{ minHeight: '100vh', padding: 20, maxWidth: 1200, margin: 'auto', fontFamily: 'Arial, sans-serif' }}>
      <div style={{ display: 'flex', gap: 20 }}>
        {/* Sidebar */}
        <aside style={{
          flex: '0 0 250px',
          backgroundColor: dark ? '#2d3748' : '#fff',
          borderRadius: 16,
          padding: 20,
          position: 'sticky',
          top: 20,
          height: 'fit-content',
          boxShadow: dark ? '0 1px 6px rgba(0,0,0,0.7)' : '0 1px 6px rgba(0,0,0,0.1)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
            <div style={{
              width: 48,
              height: 48,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #3b82f6, #6366f1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontWeight: 'bold',
              fontSize: 20,
              userSelect: 'none',
            }}>M</div>
            <div>
              <div style={{ fontWeight: '600', fontSize: 14 }}>Marwa</div>
              <div style={{ fontSize: 12, color: dark ? '#a0aec0' : '#718096' }}>Student • Mazar-e-Sharif</div>
            </div>
          </div>

          <nav>
            {['Overview', 'My Courses', 'Catalog', 'Certificates', 'Settings'].map((item) => (
              <a
                key={item}
                href="#"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '8px 12px',
                  borderRadius: 8,
                  textDecoration: 'none',
                  color: dark ? '#e2e8f0' : '#1a202c',
                  marginBottom: 8,
                  fontSize: 14,
                  cursor: 'pointer',
                  userSelect: 'none',
                }}
                onMouseEnter={e => e.currentTarget.style.backgroundColor = dark ? '#4a5568' : '#edf2f7'}
                onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                <FiGrid />
                {item}
              </a>
            ))}
          </nav>

          <div style={{ marginTop: 20 }}>
            <div style={{ fontSize: 11, fontWeight: '600', textTransform: 'uppercase', color: dark ? '#a0aec0' : '#718096', marginBottom: 6 }}>
              Learning Path
            </div>
            <div style={{
              width: '100%',
              height: 10,
              backgroundColor: dark ? '#4a5568' : '#e2e8f0',
              borderRadius: 5,
              overflow: 'hidden',
            }}>
              <div style={{
                width: '48%',
                height: '100%',
                background: 'linear-gradient(90deg, #3b82f6, #6366f1)',
              }} />
            </div>
            <div style={{ fontSize: 11, color: dark ? '#a0aec0' : '#718096', marginTop: 6 }}>
              48% complete
            </div>
          </div>

          <div style={{
            marginTop: 30,
            borderTop: dark ? '1px solid #4a5568' : '1px solid #e2e8f0',
            paddingTop: 12,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontSize: 14,
            fontWeight: '600',
          }}>
            <span>Dark Mode</span>
            <button
              onClick={() => setDark(!dark)}
              aria-label="Toggle dark mode"
              style={{
                cursor: 'pointer',
                backgroundColor: 'transparent',
                border: 'none',
                fontSize: 18,
                color: dark ? '#f6e05e' : '#4a5568',
                padding: 4,
              }}
            >
              {dark ? <FiSun /> : <FiMoon />}
            </button>
          </div>
        </aside>

        {/* Main Content */}
        <main style={{ flex: 1 }}>
          {/* Header */}
          <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
            <div>
              <h1 style={{ fontSize: 26, fontWeight: '700', marginBottom: 6 }}>Welcome back, Marwa</h1>
              <p style={{ fontSize: 14, color: dark ? '#a0aec0' : '#718096' }}>
                Keep up the great work — you’re making progress!
              </p>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ position: 'relative' }}>
                <input
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                  placeholder="Search courses, topics..."
                  aria-label="Search courses"
                  style={{
                    padding: '8px 12px 8px 32px',
                    borderRadius: 8,
                    border: dark ? '1px solid #4a5568' : '1px solid #e2e8f0',
                    backgroundColor: dark ? '#2d3748' : '#fff',
                    color: dark ? '#e2e8f0' : '#1a202c',
                    width: 280,
                    fontSize: 14,
                  }}
                />
                <FiSearch style={{
                  position: 'absolute',
                  left: 8,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: dark ? '#a0aec0' : '#718096',
                }} />
              </div>
              <button
                aria-label="Notifications"
                style={{
                  cursor: 'pointer',
                  background: 'transparent',
                  border: 'none',
                  fontSize: 20,
                  color: dark ? '#e2e8f0' : '#1a202c',
                }}
              >
                <FiBell />
              </button>
              <button
                aria-label="Profile"
                style={{
                  cursor: 'pointer',
                  backgroundColor: dark ? '#2d3748' : '#fff',
                  borderRadius: '50%',
                  border: 'none',
                  width: 36,
                  height: 36,
                  boxShadow: dark ? '0 1px 4px rgba(0,0,0,0.7)' : '0 1px 6px rgba(0,0,0,0.1)',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  color: dark ? '#e2e8f0' : '#1a202c',
                }}
              >
                <FiUser />
              </button>
            </div>
          </header>

          {/* Stats */}
          <section style={{ display: 'flex', gap: 20, marginBottom: 24 }}>
            {stats.map(s => (
              <motion.div
                key={s.id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: s.id * 0.08 }}
                style={{
                  backgroundColor: dark ? '#2d3748' : '#fff',
                  borderRadius: 16,
                  padding: 16,
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 16,
                  boxShadow: dark ? '0 1px 6px rgba(0,0,0,0.7)' : '0 1px 6px rgba(0,0,0,0.1)',
                }}
              >
                <div style={{
                  width: 48,
                  height: 48,
                  borderRadius: 12,
                  backgroundColor: s.color,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  color: 'white',
                  fontSize: 24,
                }}>
                  <s.icon />
                </div>
                <div>
                  <div style={{ fontSize: 12, color: dark ? '#a0aec0' : '#718096' }}>{s.title}</div>
                  <div style={{ fontWeight: '700', fontSize: 22 }}>{s.value}</div>
                </div>
              </motion.div>
            ))}
          </section>

          {/* Progress Chart and Pie + Activities */}
          <section style={{ display: 'flex', gap: 20, marginBottom: 24, flexWrap: 'wrap' }}>
            {/* Progress Chart */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              style={{
                flex: '2 1 600px',
                backgroundColor: dark ? '#2d3748' : '#fff',
                borderRadius: 16,
                padding: 20,
                boxShadow: dark ? '0 1px 6px rgba(0,0,0,0.7)' : '0 1px 6px rgba(0,0,0,0.1)',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <h3 style={{ fontWeight: '700', marginBottom: 4 }}>Learning Progress</h3>
                  <p style={{ fontSize: 14, color: dark ? '#a0aec0' : '#718096' }}>
                    Your monthly progress over time
                  </p>
                </div>
                <div style={{ fontWeight: '700', fontSize: 14 }}>Overall: 75%</div>
              </div>

              <div style={{ height: 200, marginTop: 20 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={progressData}>
                    <XAxis dataKey="name" stroke={dark ? '#a0aec0' : '#718096'} />
                    <YAxis stroke={dark ? '#a0aec0' : '#718096'} />
                    <Tooltip
                      contentStyle={{ backgroundColor: dark ? '#4a5568' : '#fff', borderRadius: 8 }}
                      labelStyle={{ color: dark ? '#e2e8f0' : '#1a202c' }}
                      itemStyle={{ color: '#2563eb' }}
                    />
                    <Line
                      type="monotone"
                      dataKey="progress"
                      stroke="#3b82f6"
                      strokeWidth={3}
                      dot={{ r: 5 }}
                      activeDot={{ r: 7 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </motion.div>

            {/* Pie chart + activities */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              style={{
                flex: '1 1 300px',
                backgroundColor: dark ? '#2d3748' : '#fff',
                borderRadius: 16,
                padding: 20,
                boxShadow: dark ? '0 1px 6px rgba(0,0,0,0.7)' : '0 1px 6px rgba(0,0,0,0.1)',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
              }}
            >
              <div>
                <h3 style={{ fontWeight: '700', marginBottom: 16 }}>Certificate Completion</h3>
                <PieChart width={280} height={180}>
                  <Pie
                    data={[
                      { name: 'Completed', value: 3 },
                      { name: 'In Progress', value: 7 },
                      { name: 'Not Started', value: 2 },
                    ]}
                    innerRadius={50}
                    outerRadius={70}
                    fill="#8884d8"
                    paddingAngle={3}
                    dataKey="value"
                  >
                    {COLORS.map((color, index) => (
                      <Cell key={`cell-${index}`} fill={color} />
                    ))}
                  </Pie>
                </PieChart>
              </div>

              <div style={{ marginTop: 24 }}>
                <h4 style={{ fontWeight: '600', marginBottom: 12 }}>Recent Activities</h4>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0, maxHeight: 150, overflowY: 'auto' }}>
                  {activities.map(a => (
                    <li
                      key={a.id}
                      style={{
                        marginBottom: 10,
                        fontSize: 14,
                        color: dark ? '#cbd5e0' : '#2d3748',
                      }}
                    >
                      {a.text} <br />
                      <small style={{ color: dark ? '#718096' : '#718096' }}>{a.time}</small>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          </section>

          {/* Courses and Recommendations */}
          <section style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
            {/* Courses */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              style={{
                flex: '2 1 600px',
                backgroundColor: dark ? '#2d3748' : '#fff',
                borderRadius: 16,
                padding: 20,
                boxShadow: dark ? '0 1px 6px rgba(0,0,0,0.7)' : '0 1px 6px rgba(0,0,0,0.1)',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
                <h3 style={{ fontWeight: '700' }}>My Courses</h3>
                <select
                  value={filter}
                  onChange={e => setFilter(e.target.value)}
                  aria-label="Filter courses by level"
                  style={{
                    borderRadius: 8,
                    border: dark ? '1px solid #4a5568' : '1px solid #e2e8f0',
                    backgroundColor: dark ? '#2d3748' : '#fff',
                    color: dark ? '#e2e8f0' : '#1a202c',
                    padding: '6px 12px',
                    fontSize: 14,
                  }}
                >
                  <option value="All">All Levels</option>
                  <option value="Beginner">Beginner</option>
                  <option value="Intermediate">Intermediate</option>
                  <option value="Advanced">Advanced</option>
                </select>
              </div>
              <div style={{ overflowY: 'auto', maxHeight: 300 }}>
                {filteredCourses.length === 0 ? (
                  <p style={{ color: dark ? '#a0aec0' : '#718096' }}>No courses found.</p>
                ) : (
                  filteredCourses.map(course => (
                    <div
                      key={course.id}
                      style={{
                        marginBottom: 16,
                        paddingBottom: 12,
                        borderBottom: dark ? '1px solid #4a5568' : '1px solid #e2e8f0',
                      }}
                    >
                      <h4 style={{ margin: 0, fontWeight: '700' }}>{course.title}</h4>
                      <div style={{ fontSize: 12, color: dark ? '#a0aec0' : '#718096', marginBottom: 4 }}>
                        By {course.author} | Level: {course.level}
                      </div>
                      <div style={{
                        height: 8,
                        backgroundColor: dark ? '#4a5568' : '#e2e8f0',
                        borderRadius: 4,
                        overflow: 'hidden',
                      }}>
                        <div
                          style={{
                            width: `${course.progress}%`,
                            height: '100%',
                            backgroundColor: '#3b82f6',
                            transition: 'width 0.3s ease',
                          }}
                        />
                      </div>
                    </div>
                  ))
                )}
              </div>
            </motion.div>

            {/* Recommendations */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              style={{
                flex: '1 1 300px',
                backgroundColor: dark ? '#2d3748' : '#fff',
                borderRadius: 16,
                padding: 20,
                boxShadow: dark ? '0 1px 6px rgba(0,0,0,0.7)' : '0 1px 6px rgba(0,0,0,0.1)',
              }}
            >
              <h3 style={{ fontWeight: '700', marginBottom: 16 }}>Recommendations</h3>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {recommendations.map((rec, i) => (
                  <li
                    key={i}
                    style={{
                      padding: '8px 12px',
                      borderRadius: 8,
                      marginBottom: 12,
                      backgroundColor: dark ? '#4a5568' : '#f3f4f6',
                      cursor: 'pointer',
                      userSelect: 'none',
                      fontWeight: '600',
                      color: dark ? '#e2e8f0' : '#1a202c',
                      transition: 'background-color 0.2s ease',
                    }}
                    onMouseEnter={e => e.currentTarget.style.backgroundColor = dark ? '#636e72' : '#e5e7eb'}
                    onMouseLeave={e => e.currentTarget.style.backgroundColor = dark ? '#4a5568' : '#f3f4f6'}
                  >
                    {rec}
                  </li>
                ))}
              </ul>
            </motion.div>
          </section>
        </main>
      </div>
    </div>
  );
}
